import React from "react";

export type DocumentoAgrupado = {
  titulo: string;
  subtitulo?: string;
  conteudo: (string | React.ReactElement)[];
};

type DocumentGroupProps = {
  titulo: string;
  subtitulo?: string;
  conteudo: (string | React.ReactElement)[];
};

const ASSOCIADO_TITLE_CLASS = "visao-geral-associado-title";

function isAssociadoTitleParagraph(node: React.ReactNode): boolean {
  if (!React.isValidElement(node)) return false;
  const cn = node.props?.className;
  return typeof cn === "string" && cn.split(/\s+/).includes(ASSOCIADO_TITLE_CLASS);
}

function wrapAssociadoBlocos(children: React.ReactNode[]): React.ReactNode[] {
  const result: React.ReactNode[] = [];
  let i = 0;
  while (i < children.length) {
    const child = children[i];
    if (isAssociadoTitleParagraph(child)) {
      const block: React.ReactNode[] = [child];
      i++;
      while (i < children.length && !isAssociadoTitleParagraph(children[i])) {
        block.push(children[i]);
        i++;
      }
      result.push(
        <div key={`visao-geral-assoc-${result.length}`} className="visao-geral-associado-bloco">
          {block}
        </div>,
      );
      continue;
    }
    result.push(child);
    i++;
  }
  return result;
}

export const DocumentGroup = ({ titulo, subtitulo, conteudo }: DocumentGroupProps) => {
  const elementos: React.ReactNode[] = [];

  for (let index = 0; index < conteudo.length; index++) {
    const itemRaw = conteudo[index];
    const proximaRaw = conteudo[index + 1];

    const item = typeof itemRaw === "string" ? itemRaw.trim() : null;
    const proxima = typeof proximaRaw === "string" ? proximaRaw.trim() : null;

    if (React.isValidElement(itemRaw)) {
      elementos.push(itemRaw);
      continue;
    }

    if (item === null || item === "") {
      elementos.push(
        <p key={index}>
          <em>Não está preenchido</em>
        </p>
      );
      continue;
    }

    const isPergunta = item.endsWith("?*") || item.endsWith("?");
    const isRespostaSimples =
      proxima !== null &&
      (proxima === "SIM" || proxima === "NÃO" || /^SIM\b|^NÃO\b/.test(proxima));

    if (isPergunta && isRespostaSimples && proxima) {
      const jaContemResposta = item.toLowerCase().includes(proxima.toLowerCase());

      elementos.push(
        <p key={index}>
          <strong>{item}</strong>
          {!jaContemResposta && ` ${proxima}`}
        </p>
      );
      index++;
      continue;
    }

    if (item.startsWith("Observação:")) {
      elementos.push(
        <p key={index}>
          <strong>Observação: </strong>
          {item.replace(/^Observação:{1,2}/, "").trim()}
        </p>
      );
      continue;
    }

    if (item.startsWith("Quantas empresas associadas tem seu Sindicato?*")) {
      elementos.push(
        <p key={index}>
          <strong>Quantas empresas associadas tem seu Sindicato?</strong>
        </p>
      );
      continue;
    }

    if (item.startsWith("Associado")) {
      elementos.push(
        <p key={index} className={ASSOCIADO_TITLE_CLASS}>
          <strong>{item}</strong>
        </p>
      );
      continue;
    }

    if (
      item.startsWith("Tipo do documento*") ||
      item.startsWith("Qual tipo de Ação Internacional?")
    ) {
      elementos.push(
        <p key={index}>
          <strong>{item}</strong>
        </p>
      );
      continue;
    }

    if (item.startsWith("Receita Operacional:")) {
      elementos.push(
        <p key={index}>
          <strong>Receita Operacional:</strong> {item.replace("Receita Operacional:", "").trim()}
        </p>
      );
      continue;
    }

    if (item.startsWith("Qual o tipo de Ação Internacional?:")) {
      const [pergunta, respostaTexto] = item.split(":");
      elementos.push(
        <p key={index}>
          <strong>{pergunta.trim().replace(/\?+$/, "")}?</strong>{" "}
          <span className="text-muted">{respostaTexto?.trim()}</span>
        </p>
      );
      continue;
    }

    const match = item.match(/^(.+?):\s*(.+)$/);
    if (match) {
      elementos.push(
        <p key={index}>
          <strong>{match[1]}:</strong> {match[2]}
        </p>
      );
      continue;
    }

    elementos.push(
      <p key={index}>{index === 0 ? <strong>{itemRaw}</strong> : itemRaw}</p>
    );
  }

  const elementosComAssociadosAgrupados = wrapAssociadoBlocos(elementos);

  return (
    <div className="visao-geral-document-group">
      <h3>{titulo}</h3>
      {subtitulo ? <h4>{subtitulo}</h4> : null}
      {elementosComAssociadosAgrupados}
    </div>
  );
};

type SectionProps = {
  titulo: string;
  documentos: DocumentoAgrupado[];
  emptyMessage?: string;
  
  documentosListClassName?: string;
  
  folhaCartao?: boolean;
};

export const Section = ({
  titulo,
  documentos,
  emptyMessage = "Nenhuma informação encontrada.",
  documentosListClassName,
  folhaCartao,
}: SectionProps) => {
  const listaDocumentos = Array.isArray(documentos) ? documentos : [];

  const grupos = listaDocumentos.map((doc, idx) => (
    <DocumentGroup
      key={`${doc.titulo}-${idx}`}
      titulo={doc.titulo}
      subtitulo={doc.subtitulo}
      conteudo={doc.conteudo}
    />
  ));

  const conteudoLista =
    listaDocumentos.length === 0 ? (
      <p className="visao-geral-empty">{emptyMessage}</p>
    ) : documentosListClassName ? (
      <div className={documentosListClassName}>{grupos}</div>
    ) : (
      grupos
    );

  if (folhaCartao) {
    return (
      <section className="visao-geral-section consulta-envio-slot-folha">
        <div className="consulta-envio-folha-cartao">
          <h2 className="visao-geral-section-title">{titulo}</h2>
          {conteudoLista}
        </div>
      </section>
    );
  }

  return (
    <section className="visao-geral-section">
      <h2 className="visao-geral-section-title">{titulo}</h2>
      {conteudoLista}
    </section>
  );
};
